import React, { useEffect, useState } from "react";
import { FormField } from "@avamae/formbuilder";
import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";

import { ErrorM } from "components/helpers/FormHelpers/ErrorM";
import { Input } from "components/helpers/FormHelpers/Input";
import { TooltipIconData } from "@avamae/formbuilder/dist/FormBuilder";
import { useField, useFormikContext } from "formik";
import { after } from "lodash";
import editIcon from "assets/edit_icon.png";
export interface FormTextFieldProps<T> {
  fieldName: string;
  label: string;
  readOnly?: boolean;
  afterLabelText?: string;
  placeholderText?: string;
  inputProps?: JSX.IntrinsicElements["input"];
  tooltip?: string | null;
  tooltipIconData?: TooltipIconData | null;
  isNumber?: boolean;
  showEditIcon?: boolean;
  maxCharacterLimit?: number;
  wrapText?: boolean;
}

const FormTextField = function<T>({
  fieldName,
  label,
  readOnly,
  placeholderText,
  afterLabelText,
  inputProps,
  tooltipIconData,
  tooltip,
  isNumber,
  showEditIcon,
  maxCharacterLimit,
  wrapText,
}: FormTextFieldProps<T>) {
  const [field, meta] = useField(fieldName as string);
  const isPlaceholder = placeholderText && placeholderText !== "";
  const [textAreaValue, setTextAreaValue] = useState<string>("");

  useEffect(() => {
    if (field.value) {
      setTextAreaValue(field.value);
    }
  }, []);

  const formik = useFormikContext();

  const onTextChange = (e: any) => {
    formik.setFieldValue(field.name, e.target.value);
  };

  if (isNumber) {
    inputProps = { ...inputProps, type: "number" };
  }
  return (
    <div
      className={`FormBox FormSideBySide StringV2 ${
        wrapText ? "WrapText" : ""
      }`}
    >
      <div className="FormLabel">
        <label>
          {label}
          {afterLabelText && (
            <span>
              <i className="pseudo">{afterLabelText}</i>
            </span>
          )}

          {showEditIcon && (
            <img src={editIcon} alt="edit icon" className="EditIcon" />
          )}
        </label>

        {tooltip &&
          tooltipIconData &&
          tooltipIconData.mainIcon &&
          tooltipIconData.closeIcon && (
            <FormFieldTooltip
              tooltipText={tooltip}
              tooltipMainIconSource={tooltipIconData.mainIcon}
              tooltipCloseIconSource={tooltipIconData.closeIcon}
            />
          )}
      </div>
      <div className="FormField">
        {isPlaceholder && (
          <div className="EditBox placeholderTextWrapper">
            <div className="placeText">{placeholderText}</div>

            {maxCharacterLimit && !isNumber && !wrapText && (
              <div className="MaxCharacterLimit">
                Maximum text length is{" "}
                {maxCharacterLimit &&
                typeof maxCharacterLimit === "number" &&
                field.value &&
                typeof field.value === "string"
                  ? maxCharacterLimit - field.value.length >= 0
                    ? `${maxCharacterLimit - field.value.length} characters`
                    : "exceeded"
                  : `${maxCharacterLimit} characters`}{" "}
              </div>
            )}

            {maxCharacterLimit && !isNumber && wrapText && (
              <div className="MaxCharacterLimit">
                Maximum text length is{" "}
                {maxCharacterLimit &&
                typeof maxCharacterLimit === "number" &&
                textAreaValue &&
                typeof textAreaValue === "string"
                  ? maxCharacterLimit - textAreaValue.length >= 0
                    ? `${maxCharacterLimit - textAreaValue.length} characters`
                    : "exceeded"
                  : `${maxCharacterLimit} characters`}{" "}
              </div>
            )}

            {!readOnly ? (
              <input
                className="PlaceholderInputBox"
                type={isNumber ? "float" : "text"}
                onChange={onTextChange}
                value={
                  readOnly && inputProps?.value
                    ? inputProps?.value
                    : field.value
                }
              />
            ) : (
              <div className="Text">
                {readOnly && inputProps?.value
                  ? inputProps?.value
                  : field.value}
              </div>
            )}
          </div>
        )}
        {!isPlaceholder && (
          <>
            {maxCharacterLimit && !isNumber && !wrapText && (
              <div className="MaxCharacterLimit">
                Maximum text length is{" "}
                {maxCharacterLimit &&
                typeof maxCharacterLimit === "number" &&
                field.value &&
                typeof field.value === "string"
                  ? maxCharacterLimit - field.value.length >= 0
                    ? `${maxCharacterLimit - field.value.length} characters`
                    : "exceeded"
                  : `${maxCharacterLimit} characters`}{" "}
              </div>
            )}

            {maxCharacterLimit && !isNumber && wrapText && (
              <div className="MaxCharacterLimit">
                Maximum text length is{" "}
                {maxCharacterLimit &&
                typeof maxCharacterLimit === "number" &&
                textAreaValue &&
                typeof textAreaValue === "string"
                  ? maxCharacterLimit - textAreaValue.length >= 0
                    ? `${maxCharacterLimit - textAreaValue.length} characters`
                    : "exceeded"
                  : `${maxCharacterLimit} characters`}{" "}
              </div>
            )}

            {!wrapText ? (
              <>
                {!readOnly ? (
                  <Input
                    name={fieldName.toString()}
                    className={`${readOnly ? "EditBox ViewOnly" : "EditBox"} ${
                      isPlaceholder ? "hide" : ""
                    }`}
                    readOnly={readOnly}
                    {...inputProps}
                    value={
                      readOnly && inputProps?.value
                        ? inputProps?.value
                        : field.value
                    }
                    type={isNumber ? "float" : "text"}
                  />
                ) : (
                  <div className="Text">
                    {readOnly && inputProps?.value
                      ? inputProps?.value
                      : field.value}
                  </div>
                )}
              </>
            ) : (
              <>
                {!readOnly ? (
                  <textarea
                    name={fieldName.toString()}
                    className={`${readOnly ? "EditBox ViewOnly" : "EditBox"} ${
                      isPlaceholder ? "hide" : ""
                    }`}
                    value={
                      readOnly && inputProps?.value
                        ? inputProps?.value
                        : textAreaValue
                    }
                    onChange={e => {
                      setTextAreaValue(e.target.value ?? "");
                    }}
                    onBlur={() => {
                      formik.setFieldValue(fieldName, textAreaValue ?? "");
                    }}
                  />
                ) : (
                  <div className="Text">
                    {readOnly && inputProps?.value
                      ? inputProps?.value
                      : field.value}
                  </div>
                )}
              </>
            )}
          </>
        )}
        {formik && formik.submitCount > 0 && (
          <ErrorM name={fieldName.toString()} />
        )}
      </div>
    </div>
  );
};

export { FormTextField };

type PlaceholderTextFields = {
  [fieldName: string]: string;
};
export type labelAfterContentFields = {
  [fieldName: string]: string;
};

export const stringV2Renderer = (
  readOnly: boolean,
  placeholderTextFields: PlaceholderTextFields | null,
  labelAfterContentFields: labelAfterContentFields | null,
  isNumber: boolean = false,
  maxCharacterLimit?: { [field: string]: number },
  wrapText?: boolean,
  showEditIcon?: boolean
) =>
  function stringV2Renderer<T>(_key: React.ReactText, field: FormField<T>) {
    const finalMaxCharacterLimit =
      maxCharacterLimit && maxCharacterLimit.hasOwnProperty(field.name)
        ? maxCharacterLimit[field.name as keyof typeof maxCharacterLimit]
        : null;

    return (
      <FormTextField
        label={field.label as string}
        readOnly={readOnly}
        placeholderText={
          placeholderTextFields &&
          placeholderTextFields.hasOwnProperty(field.name)
            ? placeholderTextFields[
                field.name as keyof typeof placeholderTextFields
              ]
            : undefined
        }
        fieldName={String(field.name)}
        afterLabelText={
          labelAfterContentFields &&
          labelAfterContentFields.hasOwnProperty(field.name)
            ? labelAfterContentFields[
                field.name as keyof typeof labelAfterContentFields
              ]
            : undefined
        }
        isNumber={isNumber}
        tooltip={field.toolTip}
        showEditIcon={showEditIcon}
        maxCharacterLimit={finalMaxCharacterLimit ?? undefined}
        wrapText={wrapText}
      />
    );
  };
